/**
 * Dashboard Service
 *
 * Service for fetching dashboard metrics (Metric 1..21) for the ICFR system.
 * Handles dashboard-related API calls and maps backend payloads into
 * frontend-friendly types for charts.
 */

import { apiRequest } from './apiClient';

// ==================== FRONTEND TYPES ====================

/**
 * Metric 1: Processes count breakdown by cycle
 * (Used for EX1 - Number of Cycles and Processes chart)
 */
export type DashboardMetric1Process = {
  processId: string;
  processName: string;
  processCode: string;
};

export type DashboardMetric1Cycle = {
  cycleId: string;
  cycleName: string;
  cycleCode: string;
  processCount: number;
  processes: DashboardMetric1Process[];
};

/**
 * Metric 2: Controls count breakdown by cycle
 * (Used for EX1 - Control Distribution by Cycle chart)
 */
export type DashboardMetric2CycleControlCount = {
  cycleId: string;
  cycleName: string;
  cycleCode: string;
  controlCount: number;
};

/**
 * Metric 3
 */
export type DashboardMetric3ProcessControlCount = {
  processId: string;
  processName: string;
  processCode: string;
  effectiveControls: number;
  ineffectiveControls: number;
  untestedControls: number;
  outOfScopeControls: number;
};

/**
 * Metric 4
 */
export type DashboardMetric4CycleKeyControlCount = {
  cycleId: string;
  cycleName: string;
  cycleCode: string;
  keyControlCount: number;
  nonkeyControlCount: number;
};

/**
 * Metric 5
 */
export type DashboardMetric5CycleRiskBreakdown = {
  cycleId: string;
  cycleName: string;
  cycleCode: string;
  unrankedCount: number;
  lowCount: number;
  mediumCount: number;
  highCount: number;
};

/**
 * Metric 6
 */
export type DashboardMetric6ProcessRiskBreakdown = {
  processId: string;
  processName: string;
  processCode: string;
  unrankedControlCount: number;
  lowControlCount: number;
  mediumControlCount: number;
  highControlCount: number;
};

/**
 * Metric 7
 */
export type DashboardMetric7CycleObjectiveCount = {
  cycleId: string;
  cycleName: string;
  cycleCode: string;
  objectiveCount: number;
};

/**
 * Metric 8
 */
export type DashboardMetric8CycleFraudRiskCount = {
  cycleId: string;
  cycleName: string;
  cycleCode: string;
  fraudRiskCount: number;
};

/**
 * Metric 9
 */
export type DashboardMetric9ControlsEffectiveness = {
  effectiveControls: number;
  ineffectiveControls: number;
  untestedControls: number;
  outOfScopeControls: number;
};

/**
 * Metric 10
 */
export type DashboardMetric10CycleOpenGapRiskBreakdown = {
  cycleId: string;
  cycleName: string;
  cycleCode: string;
  unrankedCount: number;
  lowCount: number;
  mediumCount: number;
  highCount: number;
};

/**
 * Metric 11
 */
export type DashboardMetric11GapWaterfall = {
  yearStartCount: number;
  quarterOneCount: number;
  quarterTwoCount: number;
  quarterThreeCount: number;
  quarterFourCount: number;
  currentCount: number;
};

/**
 * Metric 12
 */
export type DashboardMetric12AssignedProcess = {
  processId: string;
  processName: string;
  processCode: string;
};

export type DashboardMetric12ProcessOwnerListing = {
  processOwnerId: string;
  processOwnerName: string;
  completionStatus: string;
  assignedProcesses: DashboardMetric12AssignedProcess[];
};

/**
 * Metric 13
 */
export type DashboardMetric13ProcessOwnerCompletion = {
  processOwnerId: string;
  processOwnerName: string;
  quarterOneCompletionPercent: number;
  quarterTwoCompletionPercent: number;
  quarterThreeCompletionPercent: number;
  quarterFourCompletionPercent: number;
};

/**
 * Metric 14
 */
export type DashboardMetric14Scope = 'cycle' | 'process' | 'control';

export type DashboardMetric14DesignCompletionBreakdown = {
  completionPercentage: number;
};

/**
 * Metric 15
 */
export type DashboardMetric15CycleInterimTestingBreakdown = {
  cycleId: string;
  cycleName: string;
  cycleCode: string;
  controlCount: number;
  notStartedCount: number;
  wipCount: number;
  completedCount: number;
};

/**
 * Metric 16
 */
export type DashboardMetric16CycleFinalTestingBreakdown = {
  cycleId: string;
  cycleName: string;
  cycleCode: string;
  controlCount: number;
  notStartedCount: number;
  wipCount: number;
  completedCount: number;
};

/**
 * Metric 17
 */
export type DashboardMetric17WorkWeeksRemaining = {
  remainingWeeks: number;
  fiscalYearEndDate: string; // ISO string
};

/**
 * Metric 18
 */
export type DashboardMetric18AuditorCoverage = {
  totalControls: number;
  unassignedControls: number;
};

/**
 * Metric 19 - Notes to Management
 */
export type DashboardMetric19NotesToManagement = {
  content: string;
};

/**
 * Metric 21
 */
export type DashboardMetric21ApplicationScope = {
  applicationId: string;
  applicationName: string;
  isInScope: boolean;
};

/**
 * Metric 22
 */
export type DashboardMetric22CycleTestingStatus = {
  cycleId: string;
  cycleCode: string;
  cycleName: string;
  testingStatus: number;
  gapsNumber: number;
  auditorHours: number;
  auditorWeeks: number;
  processWeightSum: number;
  totalSamplesCount: number;
  isInTestingScope: boolean;
};

/**
 * Metric 23
 */
export type DashboardMetric23MonthlyHours = {
  month: number;
  monthName: string;
  assignedWeeks: number;
};

export type DashboardMetric23AuditorAssignment = {
  auditorId: string;
  auditorName: string;
  monthlyHours: DashboardMetric23MonthlyHours[];
};

/**
 * Metric 24
 */
export type DashboardMetric24Period =
  | 'quarterone'
  | 'quartertwo'
  | 'quarterthree'
  | 'quarterfour'
  | 'yeartodate';

export type DashboardMetric24GapActivity = {
  initialGapCount: number;
  openedGapsCount: number;
  closedGapsCount: number;
  finalGapCount: number;
};

// ==================== BACKEND TYPES ====================
// Swagger references a schema name:
// CycleProcessCountResponseViewModel_v1_0ListApiResponse
//
// Many of your APIs use an ApiResponse wrapper. Since we don't have the exact
// wrapper shape expanded here, we support the most common patterns safely.

type ApiResponseWrapper<T> = {
  data?: T;
  result?: T;
  code?: string;
  message?: string;
};

// The item shape (your earlier sample) for metric 1:
type Metric1BackendCycle = {
  cycleId: string;
  cycleName: string;
  cycleCode: string;
  processCount: number;
  processes: {
    processId: string;
    processName: string;
    processCode: string;
  }[];
};

type Metric2BackendCycle = {
  cycleId: string;
  cycleName: string;
  cycleCode: string;
  controlCount: number;
};

const mapMetric2BackendToFrontend = (
  item: Metric2BackendCycle,
): DashboardMetric2CycleControlCount => ({
  cycleId: item.cycleId,
  cycleName: item.cycleName,
  cycleCode: item.cycleCode,
  controlCount: item.controlCount,
});

// ==================== MAPPING FUNCTIONS ====================

/**
 * Normalize the API response into a T payload.
 * Supports common response shapes:
 *  - { data: T }
 *  - { result: T }
 *  - T (bare payload)
 */
const unwrapApiResponse = <T>(response: unknown): T => {
  // If it is already the payload (array/object)
  if (response && typeof response === 'object') {
    const maybeWrapper = response as ApiResponseWrapper<T>;

    if (typeof maybeWrapper.data !== 'undefined') return maybeWrapper.data;
    if (typeof maybeWrapper.result !== 'undefined') return maybeWrapper.result;
  }

  return response as T;
};

/**
 * Map backend Metric 1 cycle to frontend type.
 * (Right now this is mostly 1:1, but we keep this function for consistency.)
 */
const mapMetric1BackendToFrontend = (
  cycle: Metric1BackendCycle,
): DashboardMetric1Cycle => {
  return {
    cycleId: cycle.cycleId,
    cycleName: cycle.cycleName,
    cycleCode: cycle.cycleCode,
    processCount: cycle.processCount,
    processes: (cycle.processes || []).map(p => ({
      processId: p.processId,
      processName: p.processName,
      processCode: p.processCode,
    })),
  };
};

// ==================== API FUNCTIONS ====================

/**
 * Metric 1
 * GET /api/v1/dashboard/metric-1/{annualReportId}
 *
 * Summary: Gets processes count breakdown by cycle
 *
 * Returns: DashboardMetric1Cycle[] (used by segmented/solid horizontal bar chart)
 */
export const getDashboardMetric1 = async (
  annualReportId: string,
): Promise<DashboardMetric1Cycle[]> => {
  try {
    const response = await apiRequest<unknown, undefined>(
      'GET',
      `/dashboard/metric-1/${annualReportId}`,
      undefined,
      true, // Authentication required
    );

    const payload = unwrapApiResponse<Metric1BackendCycle[]>(response);

    // Defensive: ensure array
    const list = Array.isArray(payload) ? payload : [];

    return list.map(mapMetric1BackendToFrontend);
  } catch (error) {
    console.error('Failed to fetch dashboard metric 1:', error);

    // Preserve existing error conventions in your codebase
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }

    throw {
      code: 'UNEXPECTED_ERROR',
      message:
        error instanceof Error
          ? error.message
          : 'Failed to fetch dashboard metric 1',
    };
  }
};

/**
 * Metric 2
 * GET /api/v1/dashboard/metric-2/{annualReportId}
 *
 * Summary: Gets controls count breakdown by cycle
 */
export const getDashboardMetric2 = async (
  annualReportId: string,
): Promise<DashboardMetric2CycleControlCount[]> => {
  try {
    const response = await apiRequest<unknown, undefined>(
      'GET',
      `/dashboard/metric-2/${annualReportId}`,
      undefined,
      true, // Authentication required
    );

    const payload = unwrapApiResponse<Metric2BackendCycle[]>(response);

    const list = Array.isArray(payload) ? payload : [];
    return list.map(mapMetric2BackendToFrontend);
  } catch (error) {
    console.error('Failed to fetch dashboard metric 2:', error);

    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }

    throw {
      code: 'UNEXPECTED_ERROR',
      message:
        error instanceof Error
          ? error.message
          : 'Failed to fetch dashboard metric 2',
    };
  }
};

/**
 * Metric 3
 *
 */
export const getDashboardMetric3 = async (
  annualReportId: string,
): Promise<DashboardMetric3ProcessControlCount[]> => {
  try {
    const response = await apiRequest<unknown, undefined>(
      'GET',
      `/dashboard/metric-3/${annualReportId}`,
      undefined,
      true,
    );

    const payload =
      unwrapApiResponse<DashboardMetric3ProcessControlCount[]>(response);
    return Array.isArray(payload) ? payload : [];
  } catch (error) {
    console.error('Failed to fetch dashboard metric 3:', error);

    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }

    throw {
      code: 'UNEXPECTED_ERROR',
      message:
        error instanceof Error
          ? error.message
          : 'Failed to fetch dashboard metric 3',
    };
  }
};

/**
 * Metric 4
 *
 */
export const getDashboardMetric4 = async (
  annualReportId: string,
): Promise<DashboardMetric4CycleKeyControlCount[]> => {
  try {
    const response = await apiRequest<unknown, undefined>(
      'GET',
      `/dashboard/metric-4/${annualReportId}`,
      undefined,
      true,
    );

    const payload =
      unwrapApiResponse<DashboardMetric4CycleKeyControlCount[]>(response);
    return Array.isArray(payload) ? payload : [];
  } catch (error) {
    console.error('Failed to fetch dashboard metric 4:', error);

    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }

    throw {
      code: 'UNEXPECTED_ERROR',
      message:
        error instanceof Error
          ? error.message
          : 'Failed to fetch dashboard metric 4',
    };
  }
};

/**
 * Metric 5
 *
 */
export const getDashboardMetric5 = async (
  annualReportId: string,
): Promise<DashboardMetric5CycleRiskBreakdown[]> => {
  try {
    const response = await apiRequest<unknown, undefined>(
      'GET',
      `/dashboard/metric-5/${annualReportId}`,
      undefined,
      true,
    );

    const payload =
      unwrapApiResponse<DashboardMetric5CycleRiskBreakdown[]>(response);
    return Array.isArray(payload) ? payload : [];
  } catch (error) {
    console.error('Failed to fetch dashboard metric 5:', error);

    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }

    throw {
      code: 'UNEXPECTED_ERROR',
      message:
        error instanceof Error
          ? error.message
          : 'Failed to fetch dashboard metric 5',
    };
  }
};

/**
 * Metric 6
 *
 */
export const getDashboardMetric6 = async (
  annualReportId: string,
): Promise<DashboardMetric6ProcessRiskBreakdown[]> => {
  try {
    const response = await apiRequest<unknown, undefined>(
      'GET',
      `/dashboard/metric-6/${annualReportId}`,
      undefined,
      true,
    );

    const payload =
      unwrapApiResponse<DashboardMetric6ProcessRiskBreakdown[]>(response);
    return Array.isArray(payload) ? payload : [];
  } catch (error) {
    console.error('Failed to fetch dashboard metric 6:', error);

    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }

    throw {
      code: 'UNEXPECTED_ERROR',
      message:
        error instanceof Error
          ? error.message
          : 'Failed to fetch dashboard metric 6',
    };
  }
};

/**
 * Metric 7
 *
 */
export const getDashboardMetric7 = async (
  annualReportId: string,
): Promise<DashboardMetric7CycleObjectiveCount[]> => {
  try {
    const response = await apiRequest<unknown, undefined>(
      'GET',
      `/dashboard/metric-7/${annualReportId}`,
      undefined,
      true,
    );

    const payload =
      unwrapApiResponse<DashboardMetric7CycleObjectiveCount[]>(response);
    return Array.isArray(payload) ? payload : [];
  } catch (error) {
    console.error('Failed to fetch dashboard metric 7:', error);

    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }

    throw {
      code: 'UNEXPECTED_ERROR',
      message:
        error instanceof Error
          ? error.message
          : 'Failed to fetch dashboard metric 7',
    };
  }
};

/**
 * Metric 8
 *
 */
export const getDashboardMetric8 = async (
  annualReportId: string,
): Promise<DashboardMetric8CycleFraudRiskCount[]> => {
  try {
    const response = await apiRequest<unknown, undefined>(
      'GET',
      `/dashboard/metric-8/${annualReportId}`,
      undefined,
      true,
    );

    const payload =
      unwrapApiResponse<DashboardMetric8CycleFraudRiskCount[]>(response);
    return Array.isArray(payload) ? payload : [];
  } catch (error) {
    console.error('Failed to fetch dashboard metric 8:', error);

    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }

    throw {
      code: 'UNEXPECTED_ERROR',
      message:
        error instanceof Error
          ? error.message
          : 'Failed to fetch dashboard metric 8',
    };
  }
};

/**
 * Metric 9
 *
 */
export const getDashboardMetric9 = async (
  annualReportId: string,
): Promise<DashboardMetric9ControlsEffectiveness> => {
  try {
    const response = await apiRequest<unknown, undefined>(
      'GET',
      `/dashboard/metric-9/${annualReportId}`,
      undefined,
      true,
    );

    const payload =
      unwrapApiResponse<DashboardMetric9ControlsEffectiveness>(response);

    return {
      effectiveControls: payload?.effectiveControls ?? 0,
      ineffectiveControls: payload?.ineffectiveControls ?? 0,
      untestedControls: payload?.untestedControls ?? 0,
      outOfScopeControls: payload?.outOfScopeControls ?? 0,
    };
  } catch (error) {
    console.error('Failed to fetch dashboard metric 9:', error);

    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }

    throw {
      code: 'UNEXPECTED_ERROR',
      message:
        error instanceof Error
          ? error.message
          : 'Failed to fetch dashboard metric 9',
    };
  }
};

/**
 * Metric 10
 *
 */
export const getDashboardMetric10 = async (
  annualReportId: string,
): Promise<DashboardMetric10CycleOpenGapRiskBreakdown[]> => {
  try {
    const response = await apiRequest<unknown, undefined>(
      'GET',
      `/dashboard/metric-10/${annualReportId}`,
      undefined,
      true,
    );

    const payload =
      unwrapApiResponse<DashboardMetric10CycleOpenGapRiskBreakdown[]>(response);

    return Array.isArray(payload)
      ? payload
          .map(x => ({
            cycleId: x.cycleId,
            cycleName: x.cycleName,
            cycleCode: x.cycleCode,
            unrankedCount: x.unrankedCount ?? 0,
            lowCount: x.lowCount ?? 0,
            mediumCount: x.mediumCount ?? 0,
            highCount: x.highCount ?? 0,
          }))
          .sort((a, b) => a.cycleCode.localeCompare(b.cycleCode))
      : [];
  } catch (error) {
    console.error('Failed to fetch dashboard metric 10:', error);

    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }

    throw {
      code: 'UNEXPECTED_ERROR',
      message:
        error instanceof Error
          ? error.message
          : 'Failed to fetch dashboard metric 10',
    };
  }
};

/**
 * Metric 11
 *
 */
export const getDashboardMetric11 = async (
  annualReportId: string,
): Promise<DashboardMetric11GapWaterfall> => {
  try {
    const res = await apiRequest<any, undefined>(
      'GET',
      `/dashboard/metric-11/${annualReportId}`,
      undefined,
      true,
    );

    // Most of your endpoints return { data: ... }
    return res?.data as DashboardMetric11GapWaterfall;
  } catch (error) {
    console.error('Failed to fetch dashboard metric 11:', error);
    if (error && typeof error === 'object' && 'code' in error) throw error;
    throw {
      code: 'UNEXPECTED_ERROR',
      message:
        error instanceof Error ? error.message : 'Failed to fetch metric 11',
    };
  }
};

/**
 * Metric 12
 * GET /api/v1/dashboard/metric-12/{annualReportId}
 *
 * Summary: Gets listing of process owners by process
 */
export const getDashboardMetric12 = async (
  annualReportId: string,
): Promise<DashboardMetric12ProcessOwnerListing[]> => {
  try {
    const res = await apiRequest<any, undefined>(
      'GET',
      `/dashboard/metric-12/${annualReportId}`,
      undefined,
      true,
    );

    return (res?.data ?? []) as DashboardMetric12ProcessOwnerListing[];
  } catch (error) {
    console.error('Failed to fetch dashboard metric 12:', error);
    if (error && typeof error === 'object' && 'code' in error) throw error;
    throw {
      code: 'UNEXPECTED_ERROR',
      message:
        error instanceof Error ? error.message : 'Failed to fetch metric 12',
    };
  }
};

/**
 * Metric 13
 *
 */
export const getDashboardMetric13 = async (
  annualReportId: string,
): Promise<DashboardMetric13ProcessOwnerCompletion[]> => {
  try {
    const res = await apiRequest<any, undefined>(
      'GET',
      `/dashboard/metric-13/${annualReportId}`,
      undefined,
      true,
    );

    // Most dashboard endpoints return { data: [...] }
    return (res?.data ?? []) as DashboardMetric13ProcessOwnerCompletion[];
  } catch (error) {
    console.error('Failed to fetch dashboard metric 13:', error);
    if (error && typeof error === 'object' && 'code' in error) throw error;
    throw {
      code: 'UNEXPECTED_ERROR',
      message:
        error instanceof Error ? error.message : 'Failed to fetch metric 13',
    };
  }
};

/**
 * Metric 14
 *
 */
export const getDashboardMetric14 = async (
  annualReportId: string,
  scope: DashboardMetric14Scope,
): Promise<DashboardMetric14DesignCompletionBreakdown> => {
  try {
    const response = await apiRequest<unknown, undefined>(
      'GET',
      `/dashboard/metric-14/${annualReportId}/${scope}`,
      undefined,
      true,
    );

    const payload =
      unwrapApiResponse<DashboardMetric14DesignCompletionBreakdown>(response);

    // Normalize + defend against null/undefined
    const raw = payload?.completionPercentage ?? 0;

    // If backend ever returns 0..1 instead of 0..100, normalize safely
    const normalized = raw <= 1 ? raw * 100 : raw;

    return {
      completionPercentage: Number.isFinite(normalized) ? normalized : 0,
    };
  } catch (error) {
    console.error('Failed to fetch dashboard metric 14:', error);

    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }

    throw {
      code: 'UNEXPECTED_ERROR',
      message:
        error instanceof Error
          ? error.message
          : 'Failed to fetch dashboard metric 14',
    };
  }
};

/**
 * Metric 15
 *
 */
export const getDashboardMetric15 = async (
  annualReportId: string,
): Promise<DashboardMetric15CycleInterimTestingBreakdown[]> => {
  try {
    const response = await apiRequest<unknown, undefined>(
      'GET',
      `/dashboard/metric-15/${annualReportId}`,
      undefined,
      true,
    );

    const payload =
      unwrapApiResponse<DashboardMetric15CycleInterimTestingBreakdown[]>(
        response,
      );

    return Array.isArray(payload)
      ? payload.map(x => ({
          cycleId: x.cycleId,
          cycleName: x.cycleName,
          cycleCode: x.cycleCode,
          controlCount: x.controlCount ?? 0,
          notStartedCount: x.notStartedCount ?? 0,
          wipCount: x.wipCount ?? 0,
          completedCount: x.completedCount ?? 0,
        }))
      : [];
  } catch (error) {
    console.error('Failed to fetch dashboard metric 15:', error);

    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }

    throw {
      code: 'UNEXPECTED_ERROR',
      message:
        error instanceof Error
          ? error.message
          : 'Failed to fetch dashboard metric 15',
    };
  }
};

/**
 * Metric 16
 *
 */
export const getDashboardMetric16 = async (
  annualReportId: string,
): Promise<DashboardMetric16CycleFinalTestingBreakdown[]> => {
  try {
    const response = await apiRequest<unknown, undefined>(
      'GET',
      `/dashboard/metric-16/${annualReportId}`,
      undefined,
      true,
    );

    const payload =
      unwrapApiResponse<DashboardMetric16CycleFinalTestingBreakdown[]>(
        response,
      );

    return Array.isArray(payload)
      ? payload.map(x => ({
          cycleId: x.cycleId,
          cycleName: x.cycleName,
          cycleCode: x.cycleCode,
          controlCount: x.controlCount ?? 0,
          notStartedCount: x.notStartedCount ?? 0,
          wipCount: x.wipCount ?? 0,
          completedCount: x.completedCount ?? 0,
        }))
      : [];
  } catch (error) {
    console.error('Failed to fetch dashboard metric 16:', error);

    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }

    throw {
      code: 'UNEXPECTED_ERROR',
      message:
        error instanceof Error
          ? error.message
          : 'Failed to fetch dashboard metric 16',
    };
  }
};

/**
 * Metric 17
 *
 */
export const getDashboardMetric17 = async (
  annualReportId: string,
): Promise<DashboardMetric17WorkWeeksRemaining> => {
  try {
    const response = await apiRequest<unknown, undefined>(
      'GET',
      `/dashboard/metric-17/${annualReportId}`,
      undefined,
      true,
    );

    const payload =
      unwrapApiResponse<DashboardMetric17WorkWeeksRemaining>(response);

    return {
      remainingWeeks: payload?.remainingWeeks ?? 0,
      fiscalYearEndDate: payload?.fiscalYearEndDate ?? '',
    };
  } catch (error) {
    console.error('Failed to fetch dashboard metric 17:', error);

    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }

    throw {
      code: 'UNEXPECTED_ERROR',
      message:
        error instanceof Error
          ? error.message
          : 'Failed to fetch dashboard metric 17',
    };
  }
};

/**
 * Metric 18
 *
 */
export const getDashboardMetric18 = async (
  annualReportId: string,
): Promise<DashboardMetric18AuditorCoverage> => {
  try {
    const response = await apiRequest<unknown, undefined>(
      'GET',
      `/dashboard/metric-18/${annualReportId}`,
      undefined,
      true,
    );

    const payload =
      unwrapApiResponse<DashboardMetric18AuditorCoverage>(response);

    return {
      totalControls: payload?.totalControls ?? 0,
      unassignedControls: payload?.unassignedControls ?? 0,
    };
  } catch (error) {
    console.error('Failed to fetch dashboard metric 18:', error);

    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }

    throw {
      code: 'UNEXPECTED_ERROR',
      message:
        error instanceof Error
          ? error.message
          : 'Failed to fetch dashboard metric 18',
    };
  }
};

/**
 * Metric 19 - Notes to Management (GET)
 * GET /api/v1/dashboard/metric-19/{annualReportId}
 */
export const getDashboardMetric19 = async (
  annualReportId: string,
): Promise<DashboardMetric19NotesToManagement> => {
  try {
    const response = await apiRequest<unknown, undefined>(
      'GET',
      `/dashboard/metric-19/${annualReportId}`,
      undefined,
      true,
    );

    const payload =
      unwrapApiResponse<DashboardMetric19NotesToManagement>(response);

    return {
      content: payload?.content ?? '',
    };
  } catch (error) {
    console.error('Failed to fetch dashboard metric 19:', error);

    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }

    throw {
      code: 'UNEXPECTED_ERROR',
      message:
        error instanceof Error
          ? error.message
          : 'Failed to fetch dashboard metric 19',
    };
  }
};

/**
 * Metric 19 - Notes to Management (UPDATE)
 * PUT /api/v1/dashboard/metric-19/{annualReportId}
 */
export const updateDashboardMetric19 = async (
  annualReportId: string,
  content: string,
): Promise<void> => {
  try {
    await apiRequest<unknown, { content: string }>(
      'PUT',
      `/dashboard/metric-19/${annualReportId}`,
      { content },
      true,
    );
  } catch (error) {
    console.error('Failed to update dashboard metric 19:', error);

    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }

    throw {
      code: 'UNEXPECTED_ERROR',
      message:
        error instanceof Error
          ? error.message
          : 'Failed to update dashboard metric 19',
    };
  }
};

/**
 * Metric 21
 *
 */
export const getDashboardMetric21 = async (
  annualReportId: string,
): Promise<DashboardMetric21ApplicationScope[]> => {
  try {
    const response = await apiRequest<unknown, undefined>(
      'GET',
      `/dashboard/metric-21/${annualReportId}`,
      undefined,
      true,
    );

    const payload =
      unwrapApiResponse<DashboardMetric21ApplicationScope[]>(response);

    return Array.isArray(payload)
      ? payload.map(x => ({
          applicationId: x.applicationId,
          applicationName: x.applicationName,
          isInScope: !!x.isInScope,
        }))
      : [];
  } catch (error) {
    console.error('Failed to fetch dashboard metric 21:', error);

    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }

    throw {
      code: 'UNEXPECTED_ERROR',
      message:
        error instanceof Error
          ? error.message
          : 'Failed to fetch dashboard metric 21',
    };
  }
};


/**
 * Metric 22
 *
 */
export const getDashboardMetric22 = async (
  annualReportId: string,
): Promise<DashboardMetric22CycleTestingStatus[]> => {
  try {
    const res = await apiRequest<any, undefined>(
      'GET',
      `/dashboard/metric-22/${annualReportId}`,
      undefined,
      true,
    );

    // Most dashboard endpoints return { data: [...] }
    return (res?.data ?? []) as DashboardMetric22CycleTestingStatus[];
  } catch (error) {
    console.error('Failed to fetch dashboard metric 13:', error);
    if (error && typeof error === 'object' && 'code' in error) throw error;
    throw {
      code: 'UNEXPECTED_ERROR',
      message:
        error instanceof Error ? error.message : 'Failed to fetch metric 22',
    };
  }
};

/**
 * Metric 23
 *
 */
export const getDashboardMetric23 = async (
  annualReportId: string,
): Promise<DashboardMetric23AuditorAssignment[]> => {
  try {
    const res = await apiRequest<any, undefined>(
      'GET',
      `/dashboard/metric-23/${annualReportId}`,
      undefined,
      true,
    );

    return (res?.data ?? []) as DashboardMetric23AuditorAssignment[];
  } catch (error) {
    console.error('Failed to fetch dashboard metric 23:', error);
    if (error && typeof error === 'object' && 'code' in error) throw error;
    throw {
      code: 'UNEXPECTED_ERROR',
      message:
        error instanceof Error ? error.message : 'Failed to fetch metric 23',
    };
  }
};

/**
 * Metric 24
 *
 */
export const getDashboardMetric24 = async (
  annualReportId: string,
  period: DashboardMetric24Period,
): Promise<DashboardMetric24GapActivity> => {
  try {
    const res = await apiRequest<any, undefined>(
      'GET',
      `/dashboard/metric-24/${annualReportId}/${period}`,
      undefined,
      true,
    );

    const d = res?.data;
    return {
      initialGapCount: d?.initialGapCount ?? 0,
      openedGapsCount: d?.openedGapsCount ?? 0,
      closedGapsCount: d?.closedGapsCount ?? 0,
      finalGapCount: d?.finalGapCount ?? 0,
    };
  } catch (error) {
    console.error('Failed to fetch dashboard metric 24:', error);
    if (error && typeof error === 'object' && 'code' in error) throw error;
    throw {
      code: 'UNEXPECTED_ERROR',
      message:
        error instanceof Error ? error.message : 'Failed to fetch metric 24',
    };
  }
};